import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Shield, Clock, Award, MapPin, FileCheck, Users, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, localizedPath, SITE_BASE_URL } from "@/i18n/paths";
import { services, getServiceBySlug } from "@/data/services";
import { cities } from "@/data/cities";
import { slugifyKeyword } from "@/data/seoKeywords";
import { sharedCanadianKeywords, topCanadianKeywords } from "@/data/sharedKeywords";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { JsonLd, serviceSchema, breadcrumbSchema } from "@/components/JsonLd";
import { TESTIDS } from "@/constants/testIds";

export const ServicesList = () => {
  const { t, lang } = useLang();
  const lp = (p) => localizedPath(p, lang);
  return (
    <>
      <Helmet>
        <title>{t("meta.services_title")}</title>
        <meta name="description" content={t("meta.services_desc")} />
        <meta name="keywords" content={t("meta.services_keywords")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${lang === "fr" ? "/fr/services" : "/services"}`} />
        {(hreflangLinks("/services") || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>
      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-24 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          {t("nav.services")} · {services.length}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>{t("services.list_title")}</span>
          </MaskedReveal>
        </h1>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-zinc-400 text-lg max-w-2xl">{t("services.list_sub")}</p>
        </FadeIn>

        <div className="mt-16 border-t border-white/10">
          {services.map((s, i) => (
            <FadeIn key={s.slug} delay={i * 0.02}>
              <Link
                to={lp(`/services/${s.slug}`)}
                data-testid={TESTIDS.services.card(s.slug)}
                className="group relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10 border-b border-white/10 py-8 md:py-10 hover:bg-zinc-950 transition-colors duration-300 px-2"
              >
                <div className="md:w-16 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-amber-500">
                  / {s.number}
                </div>
                <div className="md:w-[420px] hidden md:block h-24 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name_en}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-display uppercase font-black text-3xl md:text-4xl tracking-tight leading-none text-white group-hover:text-amber-500 transition-colors">
                    {lang === "fr" ? s.name_fr : s.name_en}
                  </h3>
                  <p className="mt-3 text-zinc-400 max-w-2xl">{lang === "fr" ? s.tagline_fr : s.tagline_en}</p>
                </div>
                <ArrowUpRight className="w-6 h-6 text-zinc-600 group-hover:text-amber-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  );
};

// ---- SEO content generators (extensive, per-service templated) ----

const industriesFor = (slug) => {
  const map = {
    "static-guards": ["Corporate towers", "Government buildings", "Condominiums", "Industrial facilities", "Diplomatic missions", "Data centres"],
    "mobile-patrol": ["Retail plazas", "Industrial parks", "Gated communities", "Auto dealerships", "Multi-property portfolios", "Storage facilities"],
    "mining-security": ["Gold mining operations", "Iron ore extraction", "Lithium & nickel exploration", "Copper smelting", "Diamond mines", "Aluminum refineries"],
    "event-security": ["Music festivals", "Corporate galas", "Sporting events", "Weddings", "Trade shows", "Political rallies"],
    "executive-protection": ["C-suite executives", "Celebrities & artists", "Foreign dignitaries", "Film productions", "High-net-worth families", "Technology founders"],
    "construction-security": ["Commercial builds", "REM & tunnel projects", "Bridge construction", "Residential developments", "Highway projects", "Industrial builds"],
    "retail-loss-prevention": ["Cosmetics & beauty", "Grocery chains", "Pharmacy retail", "Fashion boutiques", "Electronics retailers", "Big-box retail"],
    "corporate-security": ["Class-A office towers", "Bank & financial HQs", "Technology campuses", "Legal firms", "Media companies", "Corporate campuses"],
    "residential-security": ["Luxury estates", "Gated communities", "Condominium towers", "Chalets & country properties", "Family residences", "Historic homes"],
    "alarm-response": ["Retail businesses", "Warehouses", "Offices", "Residences", "Industrial sites", "Institutional facilities"],
    "concierge-security": ["Luxury hotels", "Boutique residences", "Executive condominiums", "Private members' clubs", "Serviced apartments"],
    "fire-watch": ["Active construction", "Sprinkler shutdowns", "Hot-work welding", "Post-fire recovery", "High-rise renovations"],
    "k9-security": ["Airports", "Music festivals", "Schools & universities", "Mining sites", "Corporate campuses", "Warehouses"],
    "cash-in-transit": ["Banks & credit unions", "Casinos", "Mining refineries", "Pharmacies", "Retail chains", "Art dealers"],
    "healthcare-security": ["Hospitals & CIUSSS", "CHSLD long-term care", "Private clinics", "Mental health facilities", "Medical research"],
    "education-security": ["Universities", "CEGEPs", "Private schools", "Public school boards", "Language schools"],
    "hotel-hospitality": ["Luxury hotels", "Boutique hotels", "Resorts", "Casinos", "Convention hotels"],
    "marine-port-security": ["Container ports", "Grain terminals", "Marinas", "Cruise terminals", "Shipyards"],
    "warehouse-logistics": ["Distribution centres", "Cold storage", "E-commerce fulfillment", "Cross-docks", "Rail-adjacent hubs"],
    "government-municipal": ["Federal facilities", "Provincial buildings", "Courthouses", "Municipal city halls", "Public service centres"],
    "oil-gas-security": ["Oil refineries", "Pipeline stations", "LNG facilities", "Hydro substations", "Power generation"],
    "workplace-violence-prevention": ["High-risk terminations", "Court-order enforcement", "HR investigations", "Domestic-adjacent workplace threats"],
    "cybersecurity-consulting": ["Access control integration", "CCTV systems", "Insider threat", "Loi 25 compliance", "Physical/digital convergence"],
    "crowd-management": ["Stadiums & arenas", "Protests & demonstrations", "Festival egress", "Concert venues", "Political events"],
  };
  return map[slug] || ["Corporate", "Industrial", "Retail", "Institutional", "Residential", "Events"];
};

const processStepsFor = (lang) =>
  lang === "fr"
    ? [
        { n: "01", title: "Évaluation du site", body: "Un consultant se rend sur place, cartographie les vulnérabilités et documente les exigences réglementaires (BSP, CNESST, Loi 25)." },
        { n: "02", title: "Plan opérationnel", body: "Devis détaillé : effectifs, postes, horaires, technologie, protocoles d'escalade, indicateurs de performance." },
        { n: "03", title: "Sélection des agents", body: "Agents choisis selon le site : formation linguistique, préparation aux conditions locales, vérification BSP validée." },
        { n: "04", title: "Formation site-spécifique", body: "Onboarding de 8 à 40 heures selon la complexité — géographie, tenants, procédures d'urgence, radio, incidents historiques." },
        { n: "05", title: "Déploiement", body: "Superviseur de démarrage sur place les 72 premières heures. Rapports quotidiens dès le premier quart." },
        { n: "06", title: "Amélioration continue", body: "Revue trimestrielle des incidents. Ajustement des effectifs. Portail client transparent 24/7." },
      ]
    : [
        { n: "01", title: "Site assessment", body: "A consultant walks your site, maps vulnerabilities, and documents regulatory requirements (BSP, CNESST, Loi 25)." },
        { n: "02", title: "Operations plan", body: "Detailed scope: staffing count, post orders, shift schedule, technology stack, escalation protocols, KPIs." },
        { n: "03", title: "Officer selection", body: "Officers matched to your site — language mix, local-conditions readiness, BSP verification recorded." },
        { n: "04", title: "Site-specific training", body: "8- to 40-hour onboarding depending on complexity — geography, tenants, emergency procedures, radio, historical incident briefing." },
        { n: "05", title: "Deployment", body: "Start-up supervisor on-site for the first 72 hours. Daily activity reports (DARs) filed from shift one." },
        { n: "06", title: "Continuous review", body: "Quarterly incident review. Roster adjustment. 24/7 client portal for real-time transparency." },
      ];

const faqFor = (s, lang) => {
  const en = [
    {
      q: `How much does ${s.name_en.toLowerCase()} cost in Quebec?`,
      a: `${s.name_en} in Quebec is quoted per post, per shift, and per site risk profile. Typical unarmed static rates fall between $28 and $42 per hour in Quebec urban centres, with mining, executive protection, and cash-in-transit priced separately due to specialized training and equipment. Every quote we deliver is scoped from a physical or documented site survey — never a blanket rate card.`,
    },
    {
      q: `Are your officers BSP-licensed?`,
      a: `Every officer deployed on any ${s.name_en.toLowerCase()} contract holds an active Bureau de la sécurité privée (BSP) license appropriate to their category (A for unarmed, C for armed where applicable). Officer license cards are recorded in our client portal and produceable on-demand for audits or insurer verification.`,
    },
    {
      q: `Do you cover cities beyond Montreal for ${s.name_en.toLowerCase()}?`,
      a: `Yes. Our ${s.name_en.toLowerCase()} deployment reaches every incorporated municipality in Quebec — from Montreal, Laval, Quebec City, Gatineau and Sherbrooke through Trois-Rivières, Saguenay, and up into Val-d'Or, Rouyn-Noranda, Sept-Îles and Fermont. Sub-45-minute deployment reach across 17 Quebec regions.`,
    },
    {
      q: `How quickly can you deploy ${s.name_en.toLowerCase()}?`,
      a: `Standard urban deployments launch within 48–72 hours of contract signature. Emergency deployments (e.g., post-incident fire watch, workplace-violence standby, immediate mobile patrol) can be staffed same-day from our Montreal and Quebec City rapid-response benches.`,
    },
    {
      q: `Are your officers bilingual?`,
      a: `Every officer on our roster is bilingual to CEFR B2 minimum in French and English. Officers on tenant-facing posts (concierge, corporate reception, hotel, hospitality) are held to a higher CEFR C1 conversational standard in both languages.`,
    },
    {
      q: `Do you carry general liability insurance?`,
      a: `Yes. Quebec Security Services carries $10M commercial general liability, professional liability, and workers' compensation coverage through CNESST. Certificates of insurance are provided upon contract execution and are automatically renewed at policy anniversary.`,
    },
  ];
  const fr = [
    {
      q: `Combien coûte ${s.name_fr.toLowerCase()} au Québec?`,
      a: `${s.name_fr} au Québec est facturé par poste, par quart et selon le profil de risque du site. Les taux standards pour agents statiques non armés varient de 28 $ à 42 $ l'heure dans les centres urbains du Québec.`,
    },
    {
      q: `Vos agents sont-ils licenciés par le BSP?`,
      a: `Chaque agent déployé détient une licence active du Bureau de la sécurité privée (BSP) appropriée à sa catégorie. Les cartes de licence sont accessibles dans notre portail client pour audits.`,
    },
    {
      q: `Couvrez-vous les villes au-delà de Montréal?`,
      a: `Oui. Notre déploiement rejoint chaque municipalité constituée du Québec — Montréal, Laval, Québec, Gatineau, Sherbrooke, jusqu'à Val-d'Or, Rouyn-Noranda, Sept-Îles et Fermont.`,
    },
    {
      q: `Quelle est votre rapidité de déploiement?`,
      a: `Les déploiements urbains standards se lancent en 48-72 heures. Les déploiements d'urgence peuvent être dotés le jour même depuis nos équipes de réponse rapide à Montréal et Québec.`,
    },
    {
      q: `Vos agents sont-ils bilingues?`,
      a: `Chaque agent est bilingue au minimum au niveau CECR B2 en français et anglais. Les agents en poste devant les locataires sont tenus au niveau C1.`,
    },
    {
      q: `Détenez-vous une assurance responsabilité?`,
      a: `Oui. Quebec Security Services détient une couverture de responsabilité générale commerciale de 10 M$, une responsabilité professionnelle et une couverture CNESST.`,
    },
  ];
  return lang === "fr" ? fr : en;
};

const whyChooseFor = (s, lang) => {
  const en = [
    { title: "BSP-licensed, always", body: `Every officer on your ${s.name_en.toLowerCase()} contract carries an active Bureau de la sécurité privée license. Verifiable on demand.` },
    { title: "Bilingual FR/EN roster", body: "CEFR B2 minimum across the roster; C1 on tenant-facing posts. Your Quebec workforce is served in the language they prefer." },
    { title: "60+ hour pre-deployment training", body: "Use-of-force scaling, verbal de-escalation, first-aid CPR-C, ASP baton where authorized, radio protocol, incident documentation, plus site-specific onboarding." },
    { title: "24/7 Quebec Command Centre", body: "Owned and staffed by us. Median verified alarm response 11:42. Every dispatch generates an audit-ready incident report." },
    { title: "17-region rapid deployment", body: "Sub-45-minute deployment across every incorporated Quebec municipality. Staged from Montreal, Laval, Quebec City, Sherbrooke, Trois-Rivières, Gatineau, Val-d'Or and Sept-Îles." },
    { title: "Insurance-grade documentation", body: "DARs, incident reports, license logs, training logs — all archived and exportable via our client portal in insurer-audit format." },
  ];
  const fr = [
    { title: "Licencié BSP, toujours", body: "Chaque agent détient une licence BSP active. Vérifiable sur demande." },
    { title: "Effectif bilingue FR/EN", body: "Niveau CECR B2 minimum; C1 sur les postes tenants. Vos employés québécois servis dans leur langue de préférence." },
    { title: "60+ heures de formation pré-déploiement", body: "Gradation du recours à la force, désescalade verbale, premiers soins RCR-C, radio, documentation d'incident." },
    { title: "Centre de commandement 24/7", body: "Détenu et opéré par nous. Réponse médiane vérifiée 11:42. Chaque répartition génère un rapport prêt pour audit." },
    { title: "Déploiement rapide 17 régions", body: "Moins de 45 minutes vers chaque municipalité constituée du Québec." },
    { title: "Documentation prête à l'assurance", body: "Rapports d'activité, incidents, licences, formations — archivés et exportables dans notre portail client." },
  ];
  return lang === "fr" ? fr : en;
};

// ---- Service Detail ----

export const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const s = getServiceBySlug(slug);
  if (!s) return <Navigate to={lang === "fr" ? "/fr/services" : "/services"} replace />;

  const lp = (p) => localizedPath(p, lang);
  const industries = industriesFor(s.slug);
  const process = processStepsFor(lang);
  const faqs = faqFor(s, lang);
  const why = whyChooseFor(s, lang);
  const otherServices = services.filter((x) => x.slug !== s.slug).slice(0, 4);
  const topCities = cities.slice(0, 20);
  const bullets = lang === "fr"
    ? ["Agents licenciés BSP", "Bilingues FR/EN", "Formation 60+ heures", "Suivi GPS en temps réel", "Rapports d'incident numériques", "Intervention 24/7"]
    : ["BSP-licensed officers", "Bilingual FR/EN", "60+ hours training", "Real-time GPS logging", "Digital incident reports", "24/7 dispatch"];

  return (
    <>
      <Helmet>
        <title>{lang === "fr" ? `${s.name_fr} au Québec — Licencié BSP | Maze Security` : `${s.name_en} in Quebec — BSP-Licensed | Maze Security`}</title>
        <meta name="description" content={lang === "fr"
          ? `${s.short_fr} Déployé à Montréal, Laval, Québec et dans toutes les régions du Québec. Licencié BSP, bilingue FR/EN, répartition 24/7.`
          : `${s.short_en} Deployed across Montreal, Laval, Quebec City, and every Quebec region. BSP licensed, bilingual FR/EN, 24/7 dispatch.`} />
        <meta name="keywords" content={[...s.keywords, ...sharedCanadianKeywords].join(", ")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${lang === "fr" ? `/fr/services/${s.slug}` : `/services/${s.slug}`}`} />
        {(hreflangLinks(`/services/${s.slug}`) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>
      <JsonLd data={serviceSchema(s)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: s.name_en, path: `/services/${s.slug}` },
      ])} />

      {/* Hero */}
      <section className="relative min-h-[70vh] w-full overflow-hidden bg-black flex items-end">
        <img src={s.image} alt={s.name_en} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 py-20 md:py-24">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            Service / {s.number}
          </div>
          <h1
            data-testid={TESTIDS.services.detailTitle}
            className="font-display uppercase font-black text-5xl md:text-7xl lg:text-[8vw] tracking-tight leading-[0.88] text-white max-w-5xl"
          >
            <MaskedReveal>
              {(lang === "fr" ? s.name_fr : s.name_en).split(" ").map((w, i) => <span key={i}>{w}&nbsp;</span>)}
            </MaskedReveal>
          </h1>
          <FadeIn delay={0.2}>
            <p className="mt-8 text-lg md:text-2xl text-amber-500 max-w-3xl font-display uppercase tracking-tight">
              {lang === "fr" ? s.tagline_fr : s.tagline_en}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Overview + sidebar */}
      <section className="px-6 md:px-10 py-24 max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <FadeIn>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-6">→ {t("services.detail_overview")}</div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <p className="text-xl md:text-2xl text-white leading-relaxed">{lang === "fr" ? s.short_fr : s.short_en}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-10 text-base md:text-lg text-zinc-400 leading-relaxed">
              {lang === "fr" ? s.long_fr : s.long_en}
            </p>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
              {lang === "fr"
                ? `Quebec Security Services est une firme de sécurité bilingue licenciée BSP qui dessert l'ensemble du Québec avec des opérations dédiées de ${s.name_fr.toLowerCase()}. Notre modèle de dotation, notre protocole de formation et la transparence de notre portail client sont conçus pour les conditions opérationnelles du Québec — des tours du centre-ville de Montréal au corridor minier de l'Abitibi-Témiscamingue et à la ceinture de minerai de fer de la Côte-Nord. Normes du Bureau de la sécurité privée, conformité CNESST et protection des renseignements personnels (Loi 25) sont intégrées à chaque contrat.`
                : `Quebec Security Services is a BSP-licensed, bilingual security firm serving the entire province of Quebec with dedicated ${s.name_en.toLowerCase()} operations. Our staffing model, our training protocol, and our client-portal transparency are all built for the specific operating conditions of Quebec — from downtown Montreal high-rises to the mining corridor of Abitibi-Témiscamingue and the Côte-Nord iron ore belt. When you engage us for ${s.name_en.toLowerCase()}, you are not receiving a generic North American security template — you are receiving a Quebec-specific operation held to Bureau de la sécurité privée standards, CNESST workplace-safety compliance, and Loi 25 personal-information protection.`}
            </p>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
              {lang === "fr"
                ? `Qu'il s'agisse d'un seul agent pour un lieu intimiste du Vieux-Montréal, d'un dispositif de 40 agents dans un centre de distribution de Boucherville ou d'une rotation vers un camp minier au nord du 50e parallèle, le standard est identique : agents vérifiés BSP, couverture bilingue FR/EN, répartition 24/7 depuis notre centre de commandement québécois et documentation d'incidents de calibre assurance, accessible en temps réel via votre portail client.`
                : `Whether the deployment is a single officer covering a boutique venue in Old Montreal, a 40-officer footprint at a distribution centre in Boucherville, or a fly-in rotation to a mining camp north of the 50th parallel, the operating standard is identical: BSP-verified officers, bilingual FR/EN coverage, 24/7 dispatch from our Quebec Command Centre, and insurance-grade incident documentation available in real time through your client portal.`}
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-16 text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-6">→ {t("services.detail_keywords")}</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-white text-sm">
                  <span className="w-6 h-6 bg-amber-500 text-black flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>

        <aside className="md:col-span-4 md:pl-8 md:border-l md:border-white/10">
          <FadeIn>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-4">→ {lang === "fr" ? "Déployer" : "Deploy"}</div>
            <h3 className="font-display uppercase font-black text-3xl md:text-4xl tracking-tight leading-none text-white">
              {lang === "fr" ? (<>Parlez-nous<br />en.</>) : (<>Talk<br />to us.</>)}
            </h3>
            <p className="mt-4 text-zinc-400 text-sm">{lang === "fr" ? "Réponse en un jour ouvrable. Sans démarcheurs." : "One business day reply. No phone. No email harvesting."}</p>
            <Link
              to="/contact"
              data-testid={TESTIDS.services.detailCta}
              className="mt-6 group inline-flex items-center justify-between gap-4 bg-amber-500 hover:bg-amber-400 text-black h-12 px-5 w-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("services.detail_cta")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="mt-12 border-t border-white/10 pt-8">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">{lang === "fr" ? "Déployé dans" : "Deployed across"}</div>
              <div className="flex flex-wrap gap-2">
                {topCities.map((c) => (
                  <Link key={c.slug} to={lp(`/cities/${c.slug}`)} className="text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400 hover:text-amber-500 transition-colors border-b border-transparent hover:border-amber-500">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </FadeIn>
        </aside>
      </section>

      {/* Who we serve */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Clientèle desservie" : "Who we serve"}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-3xl">
            {lang === "fr" ? s.name_fr : s.name_en} — {lang === "fr" ? "industries desservies au Québec" : "industries served in Quebec"}
          </h2>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {industries.map((ind, i) => (
            <FadeIn key={ind} delay={i * 0.04}>
              <div className="border border-white/10 hover:border-amber-500 transition-colors duration-300 p-8 h-full group">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-4">
                  / {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display uppercase font-black text-2xl tracking-tight text-white group-hover:text-amber-500 transition-colors">
                  {ind}
                </h3>
                <p className="mt-4 text-sm text-zinc-400">
                  {lang === "fr"
                    ? `Opérations dédiées de ${s.name_fr.toLowerCase()} adaptées aux réalités de ce secteur au Québec — licenciées BSP, bilingues, prêtes pour l'assureur.`
                    : `Dedicated ${s.name_en.toLowerCase()} operations tailored to the operational patterns of ${ind.toLowerCase()} in Quebec — BSP-licensed, bilingual, insurer-ready.`}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Our approach / process */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Notre approche" : "Our approach"}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-3xl">
            {lang === "fr" ? "Six étapes." : "Six steps."}<br />
            <span className="text-amber-500">{lang === "fr" ? "Zéro raccourci." : "Zero shortcuts."}</span>
          </h2>
        </FadeIn>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
          {process.map((step, i) => (
            <FadeIn key={step.n} delay={i * 0.05}>
              <div className="border-t border-white/15 pt-6 group">
                <div className="flex items-start justify-between">
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">{lang === "fr" ? "Étape" : "Step"} {step.n}</div>
                  <div className="font-display font-black text-5xl md:text-6xl text-white/10 group-hover:text-amber-500 transition-colors">{step.n}</div>
                </div>
                <h3 className="mt-4 font-display uppercase font-black text-2xl md:text-3xl tracking-tight leading-tight text-white">{step.title}</h3>
                <p className="mt-3 text-zinc-400 text-sm md:text-base">{step.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Pourquoi nous" : "Why us"}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr"
              ? `La norme opérationnelle en ${s.name_fr.toLowerCase()} au Québec.`
              : `The ${s.name_en} operating standard in Quebec.`}
          </h2>
        </FadeIn>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {why.map((w, i) => (
            <FadeIn key={w.title} delay={i * 0.04}>
              <div className="border border-white/10 p-8 h-full hover:border-amber-500 transition-colors">
                <div className="w-10 h-10 bg-amber-500 text-black flex items-center justify-center mb-6">
                  <Check className="w-5 h-5" />
                </div>
                <h3 className="font-display uppercase text-xl tracking-tight text-white">{w.title}</h3>
                <p className="mt-3 text-sm text-zinc-400">{w.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Coverage — internal links to cities (SEO) */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Couverture" : "Coverage"}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr"
              ? `${s.name_fr} dans chaque ville du Québec.`
              : `${s.name_en} across every Quebec city.`}
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-8 text-zinc-400 text-lg max-w-3xl">
            {lang === "fr"
              ? `${s.name_fr} est déployé en continu à Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont et chaque municipalité constituée du Québec. Cliquez une ville pour voir la couverture locale, les industries desservies et les particularités opérationnelles.`
              : `${s.name_en} is deployed continuously across Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Trois-Rivières, Saguenay, Val-d'Or, Rouyn-Noranda, Sept-Îles, Fermont and every incorporated municipality in Quebec. Click a city to view local coverage details, industries served, and city-specific operating patterns.`}
          </p>
        </FadeIn>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {cities.map((c) => (
            <Link
              key={c.slug}
              to={lp(`/cities/${c.slug}`)}
              className="group border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-4 py-3 flex items-center justify-between transition-colors"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-300 group-hover:text-black">{c.name}</span>
              <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-black" />
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ FAQ</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr" ? "Questions fréquentes." : "Frequently asked."}
          </h2>
        </FadeIn>
        <div className="mt-14 max-w-4xl">
          {faqs.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
          ))}
        </div>
        {/* FAQ schema */}
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }} />
      </section>

      {/* SEO keyword tags */}
      <section className="border-t border-white/10 px-6 md:px-10 py-14 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ Related expertise</div>
        <div className="flex flex-wrap gap-2">
          {[...new Set([...s.keywords, ...cities.slice(0, 12).map((c) => `${s.name_en.toLowerCase()} ${c.name}`)])].map((k) => (
            <Link
              key={`svc-${k}`}
              to={lp(`/seo/${slugifyKeyword(k)}`)}
              className="group border border-white/15 hover:border-amber-500 hover:bg-amber-500 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400 hover:text-black transition-colors inline-flex items-center gap-1.5"
            >
              {k}
              <ArrowUpRight className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-white/10">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ Canadian security industry — search terms we serve</div>
          <div className="flex flex-wrap gap-2">
            {topCanadianKeywords.map((k) => (
              <Link
                key={`can-${k}`}
                to={lp(`/seo/${slugifyKeyword(k)}`)}
                className="group border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hover:text-black transition-colors inline-flex items-center gap-1.5"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Services connexes" : "Related services"}</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {otherServices.map((os) => (
            <Link key={os.slug} to={lp(`/services/${os.slug}`)} className="group relative aspect-[4/5] overflow-hidden border border-white/10 hover:border-amber-500 transition-colors">
              <img src={os.image} alt={os.name_en} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-70 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-between p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">/ {os.number}</div>
                <div>
                  <h4 className="font-display uppercase font-black text-2xl tracking-tight text-white leading-none">{lang === "fr" ? os.name_fr : os.name_en}</h4>
                  <ArrowRight className="w-4 h-4 text-white mt-3 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-amber-500 text-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] mb-4">→ {lang === "fr" ? s.name_fr : s.name_en}</div>
              <h3 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9]">
                {lang === "fr" ? (<>Déployez au Québec.<br />Parlez-nous.</>) : (<>Deploy in Quebec.<br />Talk to us.</>)}
              </h3>
            </div>
            <Link to="/contact" className="group inline-flex items-center gap-4 bg-black text-amber-500 h-14 px-6 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors self-start">
              {t("common.request_quote")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

// Local FAQ accordion component
const FaqItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-display uppercase text-lg md:text-2xl tracking-tight text-white group-hover:text-amber-500 transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-amber-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-6 pr-10 text-zinc-400 text-base leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
};
